import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Grid, Card, CardContent, Typography, Box, Button, Divider } from '@mui/material';
import {
  Schedule,
  AttachMoney,
  Place,
  EventAvailable,
  StorefrontOutlined,
} from '@mui/icons-material';
import moment from 'moment';
import * as RetailProfileActions from '../../../../../../core/retail-profiles/action';

function RetailSpacesList({ retailProfiles: retailProfilesProp }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { profiles: retailProfilesFromRedux, loading } = useSelector(
    (store) => store.retailProfiles,
  );

  // Use prop if provided, otherwise fall back to Redux
  const retailProfiles = retailProfilesProp || retailProfilesFromRedux;

  useEffect(() => {
    // Only fetch if profiles prop is not provided
    if (!retailProfilesProp) {
      // Fetch all active retail profiles (this will need a backend endpoint)
      // For now, we'll assume profiles are loaded
      dispatch(RetailProfileActions.getRetailProfiles());
    }
  }, [dispatch, retailProfilesProp]);

  // Helper function to format retail times from 24-hour to 12-hour AM/PM
  const formatRetailTime = (timeString) => {
    if (!timeString) return '';
    // timeString is in format like "14:00:00" or "14:00"
    return moment(timeString, ['HH:mm:ss', 'HH:mm']).format('h:mm A');
  };

  // Filter only active profiles with schedules
  const activeRetailSpaces =
    retailProfiles?.filter(
      (profile) => profile.status === 'active' && profile.schedules && profile.schedules.length > 0,
    ) || [];

  if (loading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body1" color="text.secondary">
          Loading retail spaces...
        </Typography>
      </Box>
    );
  }

  if (activeRetailSpaces.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <StorefrontOutlined sx={{ fontSize: 60, color: '#CCC', mb: 2 }} />
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No Retail Spaces Available
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Check back later for available retail opportunities
        </Typography>
      </Box>
    );
  }

  const handleViewDetails = (profile) => {
    navigate(`/app/retail-opportunity/${profile.id}`);
  };

  return (
    <Grid container spacing={2}>
      {activeRetailSpaces.map((profile) => (
        <Grid item xs={12} key={profile.id}>
          <Card
            sx={{
              border: '1px solid #E0E0E0',
              borderRadius: 2,
              transition: 'all 0.3s ease',
              '&:hover': {
                boxShadow: '0 4px 12px rgba(238, 123, 87, 0.2)',
                transform: 'translateY(-2px)',
              },
            }}>
            <CardContent>
              <Box mb={2}>
                <Typography variant="h6" fontWeight="bold" color="#333" gutterBottom>
                  {profile.venue?.name || 'Retail Space'}
                </Typography>
                {profile.venue?.address && (
                  <Box display="flex" alignItems="center" gap={0.5} mb={1}>
                    <Place sx={{ fontSize: 16, color: '#666' }} />
                    <Typography variant="body2" color="text.secondary">
                      {profile.venue.address}
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Date Range */}
              {(profile.start_date || profile.end_date) && (
                <Box display="flex" alignItems="center" gap={0.5} mb={1}>
                  <EventAvailable sx={{ fontSize: 16, color: '#EE7B57' }} />
                  <Typography variant="body2" color="text.secondary">
                    {profile.start_date
                      ? moment(profile.start_date).format('MMM DD, YYYY')
                      : 'Open'}{' '}
                    -{' '}
                    {profile.end_date ? moment(profile.end_date).format('MMM DD, YYYY') : 'Ongoing'}
                  </Typography>
                </Box>
              )}

              <Divider sx={{ my: 1.5 }} />

              {/* Schedules */}
              <Typography variant="subtitle2" fontWeight="bold" color="#333" mb={1}>
                Available Time Slots:
              </Typography>
              <Grid container spacing={1}>
                {profile.schedules?.slice(0, 3).map((schedule) => (
                  <Grid item xs={12} sm={6} md={4} key={schedule.id}>
                    <Box
                      sx={{
                        p: 1,
                        bgcolor: '#F5F5F5',
                        borderRadius: 1,
                        border: '1px solid #E0E0E0',
                      }}>
                      <Typography
                        variant="caption"
                        fontWeight="bold"
                        color="#333"
                        sx={{ textTransform: 'capitalize' }}>
                        {schedule.day_of_week}
                      </Typography>
                      <Box display="flex" alignItems="center" gap={0.5} mt={0.5}>
                        <Schedule sx={{ fontSize: 14, color: '#666' }} />
                        <Typography variant="caption" color="text.secondary">
                          {formatRetailTime(schedule.start_time)}
                          {' - '}
                          {formatRetailTime(schedule.end_time)}
                        </Typography>
                      </Box>
                      <Box display="flex" alignItems="center" gap={0.5} mt={0.5}>
                        <AttachMoney sx={{ fontSize: 14, color: '#3c99ba' }} />
                        <Typography variant="caption" color="text.secondary">
                          ${schedule.price_per_hour}/hr • {schedule.slots_available} slots
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>

              {profile.schedules?.length > 3 && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 1, display: 'block' }}>
                  +{profile.schedules.length - 3} more days available
                </Typography>
              )}

              {/* Action Button */}
              <Box display="flex" justifyContent="flex-end" mt={2}>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => handleViewDetails(profile)}
                  sx={{
                    bgcolor: '#EE7B57',
                    '&:hover': {
                      bgcolor: '#d66a47',
                    },
                  }}>
                  View Details & Apply
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
}

RetailSpacesList.propTypes = {
  retailProfiles: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      status: PropTypes.string,
      start_date: PropTypes.string,
      end_date: PropTypes.string,
      venue: PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
        business_address: PropTypes.string,
        city: PropTypes.shape({
          name: PropTypes.string,
        }),
        state: PropTypes.shape({
          name: PropTypes.string,
          code: PropTypes.string,
        }),
      }),
      schedules: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.number,
          day_of_week: PropTypes.string,
          start_time: PropTypes.string,
          end_time: PropTypes.string,
          price_per_hour: PropTypes.number,
          slots_available: PropTypes.number,
        }),
      ),
    }),
  ),
};

RetailSpacesList.defaultProps = {
  retailProfiles: undefined,
};

export default RetailSpacesList;
